import { api } from '@/lib/api/client';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';

/**
 * El único punto por el que Xotski habla con Xurvey.
 *
 * Xurvey es una aplicación aparte, no una sección de Xotski: aquí no se
 * diseñan encuestas, se **usan**. El protocolo está escrito en
 * `ox-xurvey/docs/07-Protocolo-Integracion-Apps.md` y es el mismo para
 * Xamplin, DiD·ox y las que vengan; este fichero es su lado de Xotski.
 *
 * Los tres conceptos que no hay que confundir:
 *
 * | | Qué es | Cuántos |
 * |---|---|---|
 * | Encuesta | El diseño: pasos, componentes, textos, tema. | uno |
 * | **Despliegue** | Esa encuesta puesta en marcha en una estación concreta. | uno por estación |
 * | Respuesta | Lo que contesta una persona. | muchos por despliegue |
 *
 * **Xotski nunca «usa una encuesta»: crea un despliegue de ella.** De ahí sale
 * el enlace público —el que va en el QR— y a ese enlace se atan las respuestas,
 * que es lo que permite saber de qué estación vino cada una sin que Xurvey
 * tenga que añadir una columna por cada aplicación del ecosistema.
 *
 * Lo que este seam NO hace, a propósito: no filtra qué encuestas se pueden
 * usar. Eso lo decide Xurvey (visibilidad de la empresa, concesiones a hijas,
 * app destino). Si Xotski filtrara por su cuenta, el día que cambie la regla
 * habría que tocar todas las apps.
 */

/** Lo que Xotski necesita saber de una encuesta para elegirla. */
export interface XurveySurvey {
  id: number;
  name: string;
  description?: string | null;
  surveyStatus: 'draft' | 'published' | 'paused' | 'archived';
  targetApp: string | null;
  targetScope: 'event' | 'station' | null;
  /** Lo devuelve `GET /xurveys/surveys/:id`; distingue xeck-in, que también declara `targetApp='xotski'`. */
  productType?: string | null;
  languages?: string[] | null;
  defaultLanguage?: string | null;
  updatedAt?: string;
}

/**
 * Lo que Xotski puede cambiar de una encuesta **sin editarla**.
 *
 * El límite no es técnico, es de contrato (protocolo §3): se puede ocultar un
 * paso, reordenar, repintar y elegir idiomas; no se pueden tocar las etiquetas
 * ni mover nada de sitio. Un evento que reescribe las preguntas ya no está
 * respondiendo la misma encuesta, y entonces las respuestas de dos eventos
 * dejan de poder compararse — que es justo para lo que existe la plantilla.
 */
export interface DeploymentOverrides {
  /** Pasos que esta estación no enseña. */
  hiddenStepIds?: number[];
  /**
   * Preguntas que esta estación no enseña, dentro de pasos que sí se ven.
   *
   * El paso al que se le ocultan TODAS desaparece solo del recorrido; eso lo
   * decide el runtime y por eso no se escribe también en `hiddenStepIds` —
   * volver a enseñar una pregunta tiene que devolver el paso sin que nadie se
   * acuerde de deshacer una segunda cosa.
   */
  hiddenComponentIds?: number[];
  /** Orden propio. Lo que no aparezca va detrás, en su orden. */
  stepOrder?: number[];
  /**
   * El botón que sale bajo el QR del premio, después del juego.
   *
   * Vive en la encuesta (`config.prizeLinkUrl` del componente de juego) y la
   * plantilla base trae `https://xotski.com`. Aquí lo pisa el evento, para que
   * cada activación mande a su landing o a su cupón sin duplicar la encuesta.
   * `''` **quita** el botón; no declararlo deja el de la encuesta.
   */
  prizeLinkUrl?: string;
  prizeLinkLabel?: string;
  /** Pisa la composición: logo, fondo, tipografía, celebración, layout. */
  design?: Record<string, unknown>;
  /**
   * Pisa el CARTEL de la encuesta: textos, fondo y la pinta del QR.
   *
   * La encuesta trae la plantilla del cartel y este evento la ajusta —su
   * titular, el patrocinador de este festival— sin tocar la de los otros. Se
   * funde clave a clave, como `design`.
   */
  poster?: Record<string, unknown>;
  /**
   * Tokens sueltos del tema: primario, fondo, texto.
   *
   * Aparte de `design` porque son dos capas distintas — `design` es cómo se
   * compone la pantalla y esto es la paleta. El `themeId` sigue siendo el de
   * la encuesta: pisar tokens está permitido, cambiar el theme no (§3), porque
   * un theme puede estar compartido con otras encuestas.
   */
  styleOverrides?: Record<string, string>;
  /** Idiomas ofrecidos aquí — sólo de entre los que trae la encuesta. */
  locales?: string[];
  /**
   * Lo que el DESPLIEGUE decide sobre el consentimiento (capa legal, sept 2026).
   *
   * Todo lo demás se deriva: contacto, móvil y premio de lo que la encuesta
   * recoge; age gate, marketing y SMS de la página Legal de la marca. Aquí sólo
   * lo que depende de la activación concreta: si se hacen fotos (y de qué
   * clase) y si puede haber menores, que apaga la cesión de imagen destacada.
   */
  consent?: {
    photos?: 'none' | 'ambient' | 'featured';
    minorsPossible?: boolean;
  };
  closingUrl?: string;
  closingLabel?: Record<string, string>;
  /** Cómo se pinta el botón del premio: enlace, botón o botón con icono. */
  prizeLinkStyle?: 'link' | 'button' | 'iconButton';
  /**
   * Qué estilo guardado se aplicó por última vez. Sólo la identidad, para que
   * el selector vuelva a marcarlo al entrar: lo que manda son los tokens ya
   * fundidos en `design`/`styleOverrides`.
   */
  stylePresetId?: number | null;
  /**
   * Qué diseño de cartel guardado se aplicó por última vez. Sólo la identidad,
   * como `stylePresetId`: lo que manda es lo ya fundido en `poster`.
   */
  posterPresetId?: number | null;
  /**
   * La imagen de la bienvenida de ESTE evento. El runtime la funde en el
   * componente `welcomeScreen` al servir — apariencia, no pregunta.
   */
  welcome?: { imageUrl?: string; imageRole?: 'hero' | 'logo' | 'background' };
}

/**
 * La capa legal de un despliegue, tal como la deriva el backend
 * (`GET /xurveys/deployments/:id/consent-config`). Es de SÓLO LECTURA para el
 * Design Journey: enseña qué instrumentos verá el invitado y por qué, qué pasos
 * gobierna el sistema (candado) y qué impediría activar.
 */
export interface ConsentInstrument {
  id:
    | 'age_gate'
    | 'notice_block'
    | 'linkage_notice'
    | 'media_tier_a'
    | 'official_rules_link'
    | 'terms_checkbox'
    | 'marketing_optin'
    | 'sms_optin'
    | 'media_tier_b';
  required: boolean;
  label?: 'terms_only' | 'terms_and_rules';
  minAge?: number | null;
  pewc?: boolean;
}
export interface ConsentIssue {
  code: string;
  severity: 'block' | 'warn' | 'info';
  message: string;
}
export interface ConsentConfig {
  inputs: {
    collectsContact: boolean;
    collectsMobile: boolean;
    hasPrize: boolean;
    photos: 'none' | 'ambient' | 'featured';
    minorsPossible: boolean;
    marketingEnabled: boolean;
    smsEnabled: boolean;
    ageGateEnabled: boolean;
    ageGateMinAge: number | null;
    hasOfficialRules: boolean;
  };
  instruments: ConsentInstrument[];
  issues: ConsentIssue[];
  /** Pasos con consentimiento o puerta de edad: el ojo se vuelve candado. */
  legalStepIds: number[];
  brand: {
    name: string | null;
    source: 'legal' | 'legacy' | 'empty';
    termsUrl: string | null;
    termsText: string | null;
    privacyUrl: string | null;
    officialRulesUrl: string | null;
    officialRulesText: string | null;
    standardTier: boolean;
    ageGateEnabled: boolean;
    ageGateMinAge: number | null;
    emailMarketingEnabled: boolean;
    smsMarketingEnabled: boolean;
  };
  langs: string[];
}

export interface XurveyDeployment {
  id: number;
  surveyId: number;
  slug: string;
  name: string | null;
  app: string;
  scope: 'event' | 'station';
  entityId: string;
  isActive: boolean;
  gameCode?: string | null;
  overrides?: DeploymentOverrides | null;
  maxResponses?: number | null;
  allowMultipleResponses?: boolean;
  deviceLockEnabled?: boolean;
  defaultLocale?: string | null;
  /** F2b: despliegue de PRUEBA (10 jugadas, 24h, premios falsos). */
  isTest?: boolean;
  testExpiresAt?: string | null;
  activatedAt?: string | null;
}

/**
 * EL despliegue de una estación, cuando tiene varios de la misma encuesta.
 *
 * Pasa de verdad: al real se le suman los de PRUEBA (F2b) y alguno cerrado de
 * una configuración anterior. `listDeployments` los devuelve del más nuevo al
 * más viejo y cada pantalla cogía «el primero que casara»: el QR impreso podía
 * llevar el slug de un enlace de prueba caducado, la previa enseñar otro, y el
 * Design Journey guardar en los reales. Resultado: se cambia el fondo, se
 * guarda, y el QR sigue enseñando OTRO despliegue sin ese fondo.
 *
 * Aquí se decide una vez: real y activo antes que nada; si no, real; si no,
 * lo que haya. Dentro de cada grupo, el más reciente (el orden que ya trae la
 * lista). Es el mismo criterio que `resolveDeployment` en el servidor.
 */
export function despliegueDeEstacion(
  despliegues: readonly XurveyDeployment[] | undefined,
  stationId: number | string | null | undefined,
): XurveyDeployment | undefined {
  if (stationId == null) return undefined;
  const suyos = (despliegues ?? []).filter((d) => String(d.entityId) === String(stationId));
  return suyos.find((d) => !d.isTest && d.isActive) ?? suyos.find((d) => !d.isTest) ?? suyos[0];
}

/**
 * ¿Es un enlace de Xotski? Es la regla 1 de la épica XCS (ADR-003), copia de
 * `ox-platform/src/apps/xurvey/consent/xotski-link.ts`: `app = 'xotski'` y
 * `productType <> 'xeckin'`. Un `productType` NULL cuenta como «no es xeck-in».
 * Los enlaces de Xamplin anteriores al 14 sep dicen `app='xotski'` y dan `true`
 * (S-3): aquí es inocuo, porque lo que esté puesto se sigue viendo. Cuando
 * consent-config devuelva la política (XCS-23) se lee la política, no esto.
 */
export function esEnlaceDeXotski(f: {
  app: string | null | undefined;
  productType: string | null | undefined;
}): boolean {
  return f.app === 'xotski' && f.productType !== 'xeckin';
}

/** Una estación (o evento) contra la que se puede desplegar. */
export interface TargetEntity {
  id: string;
  label: string;
  deployed: boolean;
  slug: string | null;
}

export interface TargetEntities {
  items: TargetEntity[];
  /**
   * `true` cuando Xurvey no pudo preguntarle a la app destino. No es lo mismo
   * «no hay estaciones» que «no pude preguntar», y la pantalla debe decirlo.
   */
  degraded?: boolean;
}

const APP = 'xotski';

export const xurveyApi = {
  /**
   * Las encuestas que esta empresa puede desplegar en Xotski.
   *
   * Sólo `published`: una encuesta en borrador está a medio escribir, y
   * ofrecerla es ofrecer un enlace que cambiará bajo los pies de quien ya lo
   * imprimió en un QR.
   */
  listSurveys: (params?: { q?: string; scope?: 'event' | 'station' }) =>
    api.get<{ items: XurveySurvey[]; total: number }>('/xurveys/surveys', {
      params: {
        targetApp: APP,
        surveyStatus: 'published',
        limit: 100,
        ...(params?.scope ? { targetScope: params.scope } : {}),
        ...(params?.q ? { q: params.q } : {}),
      },
    }),

  /**
   * Una encuesta por id, con sus metadatos.
   *
   * Hace falta aparte de `listSurveys` porque ese listado sirve para ELEGIR
   * qué desplegar y por tanto sólo trae las publicadas. El Design Journey no
   * elige: edita un despliegue que ya existe, y su encuesta puede estar
   * archivada — pasa en cuanto alguien archiva una que sigue en marcha. Con el
   * listado como única fuente, sus idiomas salían vacíos y el selector
   * desaparecía sin decir por qué.
   */
  getSurvey: (id: number) => api.get<XurveySurvey>(`/xurveys/surveys/${id}`),

  listDeployments: (surveyId: number) =>
    api.get<XurveyDeployment[]>(`/xurveys/surveys/${surveyId}/deployments`),

  targetEntities: (surveyId: number) =>
    api.get<TargetEntities>(`/xurveys/surveys/${surveyId}/target-entities`),

  /**
   * Desplegar. **La app y el ámbito no se mandan**: salen de la encuesta, así
   * que «no desplegar fuera de lo declarado» se cumple por construcción y no
   * por una validación que alguien pueda olvidar.
   *
   * Repetir la misma entidad devuelve el despliegue que ya había. Es
   * deliberado: hace que sincronizar las estaciones de un evento sea seguro
   * de repetir, sin comprobar antes qué existe.
   */
  createDeployment: (
    surveyId: number,
    body: {
      entityId: string;
      name?: string;
      defaultLocale?: string;
      /** F2b: QR de PRUEBA — 10 jugadas, 24h, premios falsos, nada se guarda. */
      isTest?: boolean;
    },
  ) => api.post<XurveyDeployment>(`/xurveys/surveys/${surveyId}/deployments`, body),

  /** F2b: activar un despliegue de prueba — se vuelve real. Idempotente. */
  activateDeployment: (id: number) =>
    api.post<XurveyDeployment>(`/xurveys/deployments/${id}/activate`, {}),

  updateDeployment: (
    id: number,
    body: Partial<{
      isActive: boolean;
      maxResponses: number;
      allowMultipleResponses: boolean;
      deviceLockEnabled: boolean;
      /** `null` = «el de la encuesta». */
      defaultLocale: string | null;
      gameCode: string | null;
      overrides: DeploymentOverrides | null;
    }>,
  ) => api.patch<XurveyDeployment>(`/xurveys/deployments/${id}`, body),

  removeDeployment: (id: number) => api.delete<{ deleted: true }>(`/xurveys/deployments/${id}`),

  /** La capa legal derivada de un despliegue: instrumentos, avisos, bloqueos y pasos con candado. */
  consentConfig: (id: number) =>
    api.get<ConsentConfig>(`/xurveys/deployments/${id}/consent-config`),

  /**
   * Las respuestas de una encuesta, una fila por sesión.
   *
   * `publicLinkId` acota a un despliegue, que es como se piden «las de esta
   * estación»: el filtro va por el enlace y no por la estación porque una
   * respuesta pertenece al despliegue del que vino, no a la entidad — que es
   * lo que permite que Xurvey no tenga una columna por cada app.
   *
   * Sin `includePartial` sólo vienen las completadas. Es el defecto correcto:
   * una encuesta a medias no es un dato, es alguien que todavía está
   * escribiendo, y contarla infla el total de cada pantalla que la mire.
   */
  /**
   * El árbol de una encuesta: sus pasos con sus componentes.
   *
   * Xotski no lo edita —para eso está Xurvey— pero sí necesita saber qué trae:
   * cuántos pasos tiene y si lleva juego. El backend traduce los tipos
   * antiguos al servirlo, así que aquí no hace falta conocer el modelo viejo.
   */
  surveyTree: (surveyId: number) =>
    api.get<{ survey: XurveySurvey; steps: XurveyTreeStep[] }>(
      `/xurveys/surveys/${surveyId}/tree`,
    ),

  /**
   * Cuánta gente termina, dónde se cae y cuánto tarda.
   *
   * El backend ya lo calculaba; faltaba enseñarlo. El abandono sale de
   * `currentStepId` de las sesiones sin terminar: por dónde iban cuando se
   * fueron.
   */
  surveyStats: (surveyId: number) =>
    api.get<SurveyStats>(`/xurveys/surveys/${surveyId}/stats`),

  /**
   * Agregación por componente de una encuesta, acotada a los despliegues que
   * se pidan: los de las estaciones de un evento, o los de todos los eventos
   * de un proyecto. Suma el servidor; aquí no se suman enlaces a mano.
   */
  aggregate: (
    surveyId: number,
    params: { publicLinkIds?: number[]; includePartial?: boolean } = {},
  ) =>
    api.get<SurveyAggregate>(`/xurveys/surveys/${surveyId}/aggregate`, {
      params: {
        ...(params.publicLinkIds?.length ? { publicLinkIds: params.publicLinkIds.join(',') } : {}),
        ...(params.includePartial ? { includePartial: 'true' } : {}),
      },
    }),

  listResponses: (
    surveyId: number,
    params: { publicLinkId?: number; page?: number; limit?: number; includePartial?: boolean },
  ) =>
    api.get<ResponsesPage>(`/xurveys/surveys/${surveyId}/responses`, {
      params: {
        ...(params.publicLinkId ? { publicLinkId: params.publicLinkId } : {}),
        ...(params.page ? { page: params.page } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.includePartial ? { includePartial: true } : {}),
      },
    }),

  // ── Pantallas del recinto y su mando ───────────────────────────────────
  //
  // Xotski es quien sabe qué estaciones son del mismo evento: un enlace guarda
  // el id de su estación y nada más. Por eso el mando se pide desde aquí, con
  // la lista de enlaces que van juntos.
  //
  // Idempotente por ese conjunto: esta pestaña se abre muchas veces y no puede
  // repartir una URL distinta cada vez.
  ensureScreen: (body: { name: string; linkIds: number[] }) =>
    api.post<ScreenControl>('/xurveys/screens', body),

  // ── Estilos guardados ──────────────────────────────────────────────────
  //
  // La apariencia que una marca deja lista, para no rediseñarla en cada
  // evento. Los guarda Xurvey porque son suyos —son sus `design` y sus
  // tokens—; Xotski sólo los lista, los aplica y los guarda desde el Design
  // Journey.
  //
  // Se ven los de la familia (matriz, hermanas e hijas); `canEdit` dice cuáles
  // puede renombrar o retirar esta empresa. Lo decide el servidor: aquí no se
  // compara `companyId` a mano porque la marca activa se cambia con el
  // selector de arriba y equivocarse significa enseñar un botón que dará 403.
  listStylePresets: () =>
    api.get<{ items: StylePreset[]; total: number }>('/xurveys/style-presets'),

  createStylePreset: (body: {
    name: string;
    design?: Record<string, unknown>;
    styleOverrides?: Record<string, string>;
  }) => api.post<StylePreset>('/xurveys/style-presets', body),

  removeStylePreset: (id: number) =>
    api.delete<{ deleted: true }>(`/xurveys/style-presets/${id}`),

  // Diseños de cartel guardados: lo mismo que los estilos, para la pestaña
  // Cartel. Mismas reglas de familia y de `canEdit`, decididas en el servidor.
  listPosterPresets: () =>
    api.get<{ items: PosterPreset[]; total: number }>('/xurveys/poster-presets'),

  createPosterPreset: (body: { name: string; poster: Record<string, unknown> }) =>
    api.post<PosterPreset>('/xurveys/poster-presets', body),

  removePosterPreset: (id: number) =>
    api.delete<{ deleted: true }>(`/xurveys/poster-presets/${id}`),
};

/**
 * El mando de un evento: lo que se reparte para proyectar y para encender.
 *
 * `token` va en la URL de la pantalla y sólo deja MIRAR; `code` habilita el
 * interruptor. Son dos porque la URL acaba en un navegador colgado de una pared
 * a la vista de cualquiera.
 */
export interface ScreenControl {
  id: number;
  name: string;
  token: string;
  code: string;
  isOnline: boolean;
  linkIds: number[] | null;
}

/** La pantalla para proyectar. Sin `slug`: el mando decide qué cartel toca. */
export function xurveyScreenUrl(token: string): string {
  const base = import.meta.env.VITE_XURVEY_PUBLIC_URL ?? 'http://localhost:5274/public.html';
  const url = new URL(base.replace(/public\.html$/, 'poster.html'));
  url.searchParams.set('screen', token);
  return url.toString();
}

/** El mando del agente. Pide el código antes de dejar tocar nada. */
export function xurveyControlUrl(token: string): string {
  const base = import.meta.env.VITE_XURVEY_PUBLIC_URL ?? 'http://localhost:5274/public.html';
  const url = new URL(base.replace(/public\.html$/, 'control.html'));
  url.searchParams.set('t', token);
  return url.toString();
}

/** Una apariencia guardada, tal como la sirve Xurvey. */
export interface StylePreset {
  id: number;
  name: string;
  /** Apariencia del despliegue: logo, fondo, contenedor, navegación… */
  design: Record<string, unknown> | null;
  /** Tokens pisados: paleta, tipografía, formas. */
  styleOverrides: Record<string, string> | null;
  companyId: number;
  /** Si esta empresa puede renombrarlo o retirarlo. Lo decide el servidor. */
  canEdit: boolean;
}

/** Un diseño de cartel guardado, tal como lo sirve Xurvey. */
export interface PosterPreset {
  id: number;
  name: string;
  /** Diseño del cartel: fondo, colores y QR. Nunca los textos. */
  poster: Record<string, unknown> | null;
  companyId: number;
  /** Si esta empresa puede renombrarlo o retirarlo. Lo decide el servidor. */
  canEdit: boolean;
}

/**
 * Las claves de `overrides.poster` que son DISEÑO y viajan en un diseño
 * guardado. `title`, `description` y `callToAction` son el texto de cada
 * evento y se quedan donde están. Misma lista que en el backend
 * (`PosterPresetService`): si el cartel gana un mando, se añade en los dos.
 */
export const POSTER_DESIGN_KEYS = [
  'backgroundImage',
  'backgroundSize',
  'backgroundPosition',
  'backgroundColor',
  'textColor',
  'qrColor',
  'qrBackground',
  'qrLogoUrl',
] as const;

/** Sólo el diseño de un cartel: lo que se guarda y lo que se aplica. */
export function posterDesignOf(poster: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of POSTER_DESIGN_KEYS) if (poster[k] !== undefined) out[k] = poster[k];
  return out;
}

/** Un paso del árbol, con lo que Xotski mira de él. */
export interface XurveyTreeStep {
  id: number;
  name: string;
  order: number;
  isTerminal: boolean;
  components: Array<{
    id: number;
    type: string;
    label?: string;
    order: number;
    options?: unknown[] | null;
  }>;
}

/** Cuánta gente termina y dónde se cae. */
/** Una opción de una pregunta de elección, con cuántos la marcaron. */
export interface OptionCount {
  value: string;
  label: string;
  count: number;
  /** Sobre quienes respondieron ESA pregunta, con un decimal. */
  pct: number;
}

/** Lo que Xurvey resume de un componente; refleja `ComponentAggregate` del backend. */
export interface ComponentAggregate {
  componentId: number;
  key: string;
  label: string;
  type: string;
  stepName: string;
  answered: number;
  kind: 'choice' | 'scale' | 'nps' | 'text';
  options?: OptionCount[];
  multiple?: boolean;
  scale?: { mean: number; median: number; min: number; max: number; count: number; distribution: Array<{ value: number; count: number }> };
  nps?: { score: number; promoters: number; passives: number; detractors: number; answered: number };
}

export interface SurveyAggregate {
  surveyId: number;
  publicLinkId: number | null;
  publicLinkIds: number[] | null;
  deploymentName: string | null;
  includePartial: boolean;
  sessions: { total: number; completed: number };
  timeline: Array<{ date: string; started: number; completed: number }>;
  components: ComponentAggregate[];
}

export interface SurveyStats {
  surveyId: number;
  sessions: { total: number; completed: number; inProgress: number; completionRate: number };
  /** La MEDIANA, no la media: una sesión abierta toda la noche no la desplaza. */
  medianMs: number | null;
  contacts: number;
  byDeployment: Array<{
    publicLinkId: number;
    name: string;
    slug: string;
    started: number;
    completed: number;
    completionRate: number;
  }>;
  /** Cuántos se fueron en cada paso. */
  dropOff: Array<{ stepId: number; name: string; abandoned: number }>;
}

/** Una página de respuestas, tal como la devuelve Xurvey. */
export interface ResponsesPage {
  total: number;
  page: number;
  limit: number;
  columns: Array<{ componentId: number; key: string; label: string; type: string }>;
  deviceColumns: string[];
  rows: Array<{
    responseId: string;
    status: string;
    deployment: string;
    contactEmail: string | null;
    completedAt: string | null;
    device: { device: string; os: string; browser: string; screen: string } | null;
    deviceDetail: Record<string, unknown> | null;
    deviceFlat: string[];
    ip: string | null;
    /** Valor por `componentId`. Las columnas dan su etiqueta y su orden. */
    values: Record<number, string>;
  }>;
}

/**
 * La dirección pública de un despliegue.
 *
 * Vive en Xurvey, no en Xotski: el runtime es suyo. Xotski enseña la encuesta
 * **por URL** —QR, enlace o iframe— en vez de renderizarla con código propio,
 * que es lo que evita que cada app acabe con su propia copia del renderer
 * divergiendo por su cuenta.
 *
 * `VITE_XURVEY_PUBLIC_URL` apunta al `public.html` de Xurvey. Sin ella se cae
 * al dev local, que es lo único razonable: inventarse el dominio de producción
 * produciría QR impresos que no resuelven.
 */
/**
 * El CARTEL de un despliegue, por URL.
 *
 * Xotski no lo redibuja: lo embebe. Es la misma decisión que con el formulario
 * y por el mismo motivo — dos dibujos del mismo cartel acaban siendo dos
 * carteles distintos, el que se diseña y el que se pega en la barra.
 */
export function xurveyPosterUrl(slug: string, opts?: { preview?: boolean }): string {
  const base = import.meta.env.VITE_XURVEY_PUBLIC_URL ?? 'http://localhost:5274/public.html';
  // Comparte host y despliegue con el runtime: se deduce del suyo en vez de
  // pedir otra variable de entorno que alguien tendrá que recordar poner.
  const url = new URL(base.replace(/public\.html$/, 'poster.html'));
  url.searchParams.set('slug', slug);
  if (opts?.preview) url.searchParams.set('preview', '1');
  return url.toString();
}

export function xurveyPublicUrl(slug: string, opts?: { preview?: boolean }): string {
  const base = import.meta.env.VITE_XURVEY_PUBLIC_URL ?? 'http://localhost:5274/public.html';
  const url = new URL(base);
  url.searchParams.set('slug', slug);
  if (opts?.preview) {
    url.searchParams.set('preview', '1');
    /*
     * `lang` — el idioma de ESTE admin viaja con la previa.
     *
     * Xurvey se sirve de OTRO origen (`:5274` en local, otro host en
     * producción), así que su runtime no puede ver nuestra preferencia: ni
     * `localStorage` ni nada cruza el límite de origen, y el iframe del Design
     * Journey caía al idioma del NAVEGADOR. Con el admin en inglés y el
     * navegador en español, el teléfono contestaba en español — reportado con
     * «Este enlace no existe o ya se retiró».
     *
     * Sólo en la previa: el enlace de verdad, el que acaba en un QR, no lleva
     * este parámetro. Allí manda la encuesta, y el invitado no tiene por qué
     * heredar el idioma de quien la diseñó.
     */
    url.searchParams.set('lang', usePrefsStore.getState().language);
  }
  return url.toString();
}
